import { useCallback, useEffect, useState } from "react";

import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "lodash.debounce";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableFilterMetaData } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { classNames } from "primereact/utils";
import { Controller, useForm } from "react-hook-form";
import { useToast } from "../../../../Shared/hook/useToast";
import { Users } from "../../Domain/Model/Users";
import userViewModel from './UserViewModel';

interface ColumnMeta {
    field: string;
    header: string;
    editable: boolean;
}

export enum Roldata {
    ADMIN = 2,
   OPERATOR = 3
}

export const UsersView = () => {

    const [filters, setFilters] = useState<DataTableFilterMeta>({ global: { value: null, matchMode: FilterMatchMode.CONTAINS }});
    const [visible, setVisible] = useState<boolean>(false);
    
    // const toast = useRef(null);
    const { showSuccess, showError, showWarn, showInfo } = useToast();

    const defaultValues = {
        firstName: "",
        lastName: "",
        dni: "",
        // gender: "",
        email: "",
        role: 0
    };

    const roles = [
        {id: Roldata.ADMIN, name: "Administrador"},
        {id: Roldata.OPERATOR, name: "Operador"}
    ];

    const {
        createUsers,
        getUsers,
        users,
        checkedStates,
        // handleSwitchChange
    } = userViewModel();
    
    type LazyParamsType = {
        first: number;
        rows: number;
    };
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lazyParams, setLazyParams] = useState<LazyParamsType>({
        first: 0,
        rows: 10,
    });
    
    const columns: ColumnMeta[] = [
        // { field: 'productId', header: 'ID', editable: false },
        { field: 'firstName', header: 'Nombre', editable: true },
        { field: 'lastName', header: 'Apellido', editable: true },
        { field: 'email', header: 'Correo', editable: true },
        { field: 'role', header: 'cargo', editable: true },
        { field: 'created', header: 'Fecha de registro', editable: true },
        { field: 'status', header: 'Estado', editable: false },
        { field: 'options', header: 'Opciones', editable: false },
    ];

    const formatDate = (value: string | Date): string => {
        let date: Date;
    
        if (typeof value === 'string') {
        // Limpiar si tiene fracción de segundos excesiva
        const cleaned = value.includes('.') ? value.split('.')[0] : value;
        date = new Date(cleaned);
        } else {
        date = value;
        }
    
        // Validar si es una fecha válida
        if (isNaN(date.getTime())) return 'Fecha inválida';
    
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
    
        return `${day}/${month}/${year}`;
    };

    const btnOptions = (id: string)=>{

        // console.log(id.toString());
        return( 
            <div className="d-inline">
                <a href={`/update-product/${id}`} className="btn btn-dark">
                    <FontAwesomeIcon icon={icon({name: 'edit'})} />
                </a>&nbsp;
            </div>
        );
    };

    const switchChange = async (checked: boolean, id: number) => {
    
        try {
            //await handleSwitchChange(checked, id);
            showSuccess("Se Actualizo", "Se ha actualizado el estado con éxito!");
        } catch(error) {
            showError("Error", "Error al actualizar!");
        }
    }
         
    const switchState = (value: Users) => {
        // console.log("value: ", value.status);
        return (
            <>
            <div className="d-inline switchState">
                <InputSwitch
                key={value.id}
                checked={checkedStates[value.id ?? 0]}
                onChange={(e) => switchChange(e.value, value.id ?? 0)}
                />
            </div>
            </>
        );
    };
    
    useEffect(() => {
                
        //ProductService.getProducts().then((data) => setProducts(data));

        // getProducts();
        const search = (filters['global'] as DataTableFilterMetaData)?.value || '';
        loadUsers(lazyParams.first ?? 0, lazyParams.rows ?? 10, search);
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams, filters]);
        
        
    const loadUsers = async (first: number, rows: number, search: string = '') => {
        setLoading(true);
        const page = Math.floor(first / rows);

        try {
            const result = await getUsers(page+1, rows, search); // ya devuelve { items, totalItems }
            
            setTotalRecords(result.totalCount);
        } catch (error) {
            console.error("Error al cargar productos", error);
        } finally {
            setLoading(false);
        }
    };

    //  Guardamos el texto en un estado normal
    const [inputValue, setInputValue] = useState("");
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
      debounce((searchValue: string) => {
        console.log("searchValue ",searchValue);
        loadUsers(lazyParams.first ?? 1, lazyParams.rows ?? 10, searchValue);
      }, 500),
      [lazyParams]
    );
    
    // Limpieza del debounce al desmontar
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);
    
    const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        
        setInputValue(value.trim());
        // solo dispara la búsqueda cuando hay un valor completo
        if (value.trim().length > 0) {
            
            debouncedSearch(value.trim());
        } else {
            // Si está vacío, recargar todo sin filtro
            loadUsers(lazyParams.first, lazyParams.rows, "");
        }
    };

    const onSubmit = async(
        data: {
            firstName: string;
            lastName: string;
            dni: string;
            // gender: string;
            email: string;
            role: number
        }
    ) => {

        console.log("DATOS A ENVIAR", data);
        
        
        try {

            const response = await createUsers(data);
            if (response === true) {
                showSuccess("Se registro", "Se ha registrado con éxito!");
                
                const search = (filters['global'] as DataTableFilterMetaData)?.value || '';
                loadUsers(lazyParams.first ?? 0, lazyParams.rows ?? 10, search);
                reset();
            }

        } catch (error) {
            console.error("Error al enviar:", error);
        }
    };
    
    const {
        control,
        formState: { errors },
        handleSubmit,
        reset
    } = useForm({ defaultValues });

     const getFormErrorMessage = (name: string) => {
        // @ts-ignore
        return errors[name] ? <small className="p-error">{errors[name].message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const header = ()=>{
        // @ts-ignore
        //const value = (filters['global'] as DataTableFilterMetaData)?.value || '';

        return (
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <h4 className="m-0">
                     <button type="button" className="btn btn-dark" onClick={() => setVisible(true)}>
                        <FontAwesomeIcon icon={icon({name: 'plus'})} /> 
                        Nuevo usuario
                    </button>
                </h4>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" value={inputValue} onChange={onGlobalFilterChange} placeholder="Buscar..." />
                </span>
            </div>
        )
    };
            
    return(
        <>
            <div className="container-fluid">
                <div className="block-header">
                    <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-12 text-start">
                            <h2>Lista usuarios</h2>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="/home" className="text-danger"><FontAwesomeIcon icon={icon({name: 'home'})} /></a>
                                </li>
                                <li className="breadcrumb-item">Lista Usuarios</li>
                            </ul>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="d-flex flex-row-reverse">
                                <div className="page_action">
                                    {/* <button type="button" className="btn btn-dark"><FontAwesomeIcon icon={icon({name: 'plus'})} /> Nuevo producto</button> */}
                                </div>
                                <div className="p-2 d-flex">
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className='title-report'>
                        Aquí puedes ver los ultimos productos añadidos
                    </div> 
                </div>
                <div className='row'>
                    <div className='col-lg-12'>
                        {/* <Toast ref={toast} /> */}
                        <DataTable 
                            value={users!} 
                            lazy 
                            paginator 
                            rows={lazyParams.rows} 
                            first={lazyParams.first} 
                            totalRecords={totalRecords} 
                            loading={loading} 
                            editMode="cell" 
                            rowsPerPageOptions={[5, 10, 15, 20]} 
                            tableStyle={{ minWidth: '50rem' }} 
                            onPage={(e) => setLazyParams({first: e.first, rows: e.rows})} 
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}" 
                            globalFilterFields={['productName', 'salePrice', 'discountPrice', 'stock']} 
                            emptyMessage="No customers found." 
                            stateStorage="session"
                            header={header} 
                            filters={filters} 
                            onFilter={(e) => {
                                setFilters(e.filters);
                                setLazyParams({ ...lazyParams, first: 1, rows: 1 });
                            }}>
                            
                            {columns.map(({ field, header, editable  }) => {
                                    return <Column 
                                    key={field} 
                                    field={field} 
                                    header={header}
                                    body={(rowData) => {

                                        if (!rowData) return null;
                                
                                        switch (field) {
                                            case "created":
                                                return formatDate(String(rowData?.created ?? ""));
                                            case "status":
                                                return switchState(rowData);
                                            case "options":
                                                return (
                                                <>
                                                    {btnOptions(rowData.productId)}
                                                    
                                                </>
                                            );
                                            default:
                                            return rowData[field];
                                        }
                                    }}

                                    // editor={editable ? (options) => cellEditor(options) : undefined} 
                                    // onCellEditComplete={onCellEditComplete} 
                                    />
                            })}
                        </DataTable>
                        <Dialog header="Agregar Usuario" visible={visible} maximizable style={{ width: '500px' }} onHide={() => setVisible(false)}>
                            <form onSubmit={handleSubmit(onSubmit)} className="text-start" >
                                
                                <div className="d-flex flex-column gap-2 ">
                                    <Controller
                                        name="firstName"
                                        control={control}
                                        rules={{ required: 'Nombre es requerido.' }}
                                        render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name}>
                                                Nombre
                                            </label>
                                            <InputText  id={field.name} name={field.name} value={field.value}  onChange={(e) => field.onChange(e.target.value)} className={classNames({ 'p-invalid': fieldState.error })} />
                                            {getFormErrorMessage(field.name)}
                                        </>
                                    )}
                                    />
                                    <Controller
                                        name="lastName"
                                        control={control}
                                        rules={{ required: 'Apellido es requerido.' }}
                                        render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name}>
                                                Apellidos
                                            </label>
                                            <InputText  id={field.name} name={field.name} value={field.value}  onChange={(e) => field.onChange(e.target.value)} className={classNames({ 'p-invalid': fieldState.error })} />
                                            {getFormErrorMessage(field.name)}
                                        </>
                                    )}
                                    />
                                    <Controller
                                        name="dni"
                                        control={control}
                                        rules={{ required: 'DNI es requerido.' }}
                                        render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name}>
                                                DNI
                                            </label>
                                            <InputText  id={field.name} name={field.name} value={field.value}  onChange={(e) => field.onChange(e.target.value)} className={classNames({ 'p-invalid': fieldState.error })} />
                                            {getFormErrorMessage(field.name)}
                                        </>
                                    )}
                                    />
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{ required: 'Correo es requerido.' }}
                                        render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name}>
                                                Correo
                                            </label>
                                            <InputText  id={field.name} name={field.name} value={field.value}  onChange={(e) => field.onChange(e.target.value)} className={classNames({ 'p-invalid': fieldState.error })} />
                                            {getFormErrorMessage(field.name)}
                                        </>
                                    )}
                                    />

                                    {/* <Controller
                                        name="gender"
                                        control={control}
                                        rules={{ required: 'Género es requerido.' }}
                                        render={({ field, fieldState }) => (
                                            <div className="d-flex">
                                                <div className="flex align-items-center mb-2 me-2">
                                                    <RadioButton
                                                    inputId="gender1"
                                                    name={field.name}
                                                    value="Masculino"
                                                    onChange={(e) => field.onChange(e.value)}
                                                    checked={field.value === 'Masculino'}
                                                    className={classNames({ 'p-invalid': fieldState.error })}
                                                    />
                                                    <label htmlFor="gender1" className="ml-2 ms-2">Masculino</label>
                                                </div>

                                                <div className="flex align-items-center mb-2">
                                                    <RadioButton
                                                    inputId="gender2"
                                                    name={field.name}
                                                    value="Femenino"
                                                    onChange={(e) => field.onChange(e.value)}
                                                    checked={field.value === 'Femenino'}
                                                    className={classNames({ 'p-invalid': fieldState.error })}
                                                    />
                                                    <label htmlFor="gender2" className="ml-2 ms-2">Femenino</label>
                                                </div>

                                                {getFormErrorMessage(field.name)}
                                            </div>
                                        )}
                                        /> */}
                                    
                                    <Controller
                                        name="role"
                                        control={control}
                                        rules={{ validate: value => value !== 0 || 'Categoría es requerido.' }}
                                        render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name}>
                                                Rol
                                            </label>
                                            <Dropdown  id={field.name} name={field.name} value={field.value} focusInputRef={field.ref} 
                                            options={roles} optionLabel="name" optionValue="id" placeholder="Selecciona una categoría" className={classNames({ 'w-full md:w-14rem p-invalid': fieldState.error })} onChange={(e) => field.onChange(e.value ? Number(e.value) : null)} />
                                            {getFormErrorMessage(field.name)}
                                        </>
                                    )}
                                    />
                                    <Button label="Guardar" type="submit" icon="pi pi-check" className={"btn btn-dark"} />
                                </div>
                            </form>
                        </Dialog>
                    </div>
                </div>
            </div>
        </>
    );
}
